import {
  addProductsToCart,
  addToCart,
  searchListsAPI,
  searchListItemsAPI,
  parseListSearchResults,
  getListsAPI,
  getListAPI,
} from 'hb4e-ui-context'
import { showErrorNotification, showNotification } from 'hb4e-ui-notifications'
import { renderList, Page } from 'hb4e-ui-core'

import {
  deleteListAPI,
  deleteListItemAPI,
  putListAPI,
  putShareListAPI,
  getPurchasedItemsListAPI,
  putListSortOrderAPI,
} from './api'
import {
  SET_MY_LISTS,
  SET_MY_LISTS_FETCHING,
  SET_ACTIVE_LIST_FETCHING,
  SET_ACTIVE_LIST,
  SET_ADDITIONAL_LISTS,
  SET_LIST_SEARCH_RESULTS,
  RESET_LIST_SEARCH_RESULTS,
  TOGGLE_IS_REORDERING,
  SET_REORDERED_LIST_ITEMS,
  RESET_LIST_REORDERING,
  LIST_ITEMS_PAGE_LIMIT,
} from './constants'
import { recordListUpdatedEvent } from './analytics'
import { selectReorderableItems } from './reducers'

// Limit to 20 as an estimate because api/list/ response doesn't count FREE TRIALS, unless, the account have an active free trial
export const fetchLists =
  (page = new Page({ offset: 0, limit: 20 })) =>
  async (dispatch) => {
    dispatch({ type: SET_MY_LISTS_FETCHING })

    const pagination = await getListsAPI(page)

    dispatch({
      type: SET_MY_LISTS,
      lists: pagination.content,
      totalLists: pagination.totalElements,
      nextOffset: pagination.filters?.nextOffset,
      page: pagination,
      limit: pagination.limit,
    })
  }

export const fetchAdditionalLists =
  (page = new Page({ offset: 0, limit: 10 })) =>
  async (dispatch) => {
    const pagination = await getListsAPI(page)

    dispatch({
      type: SET_ADDITIONAL_LISTS,
      lists: pagination.content,
      nextOffset: pagination.filters?.nextOffset,
      limit: pagination.limit,
    })
  }

export const updateList = (list) => async (dispatch) => {
  await putListAPI(list)

  recordListUpdatedEvent(list)

  await dispatch(resetListSearchResults())
  return dispatch(fetchLists())
}

export const shareList = (listID, body) => (dispatch) => {
  return putShareListAPI(listID, body)
}

export const deleteList = (listID) => async (dispatch) => {
  await deleteListAPI(listID)

  return dispatch(fetchLists())
}

export const addListProductToCart = (product) => async (dispatch) => {
  await dispatch(addToCart(product, 'List Page'))
}

export const addListItemsToCart = (list) => async (dispatch) => {
  const listItems = list.getItems().map((item) => ({ ...item, productAvailabilityId: item.availabilityId }))

  const { success, errors } = await dispatch(addProductsToCart(listItems, 'List Page'))

  dispatch(showNotification(`${success.length} items successfully added to cart.`))

  if (errors.length) {
    dispatch(showNotification(`${renderList(errors)} can not be purchased with a personal cart.`))
  }
}

export const fetchList = (listID, page) => async (dispatch) => {
  dispatch({ type: SET_ACTIVE_LIST_FETCHING })

  const list = await getListAPI(listID, page)

  dispatch({ type: SET_ACTIVE_LIST, list })

  /**
   * Once we've fetch the list details for an active list, we want to also reset
   * any data relating to any list reordering that might have been in progress.
   *
   * We are fetching new list data here, that means the user has either clicked through
   * to the next page of items or has navigated to a different list. Either way,
   * any list reordering data we had before is now stale.
   */
  dispatch(resetListReordering())
}

export const removeListItem = (listID, itemID) => async (dispatch) => {
  await deleteListItemAPI(listID, itemID)

  dispatch(showNotification(`Item ${itemID} removed from list.`))

  return Promise.all([dispatch(fetchList(listID)), dispatch(fetchLists())])
}

export const resetListSearchResults = () => async (dispatch) => {
  return dispatch({ type: RESET_LIST_SEARCH_RESULTS })
}

export const searchLists = (term) => async (dispatch) => {
  await dispatch(resetListSearchResults())

  const [listResults, itemResults] = await Promise.all([searchListsAPI(term), searchListItemsAPI(term)])

  return dispatch({
    type: SET_LIST_SEARCH_RESULTS,
    searchResults: parseListSearchResults({
      listResults,
      itemResults,
    }),
  })
}

export const fetchPurchasedItemsList = () => async (dispatch) => {
  try {
    return await getPurchasedItemsListAPI()
  } catch (error) {
    dispatch(showErrorNotification(error.message))
  }
}

export const resetListReordering = () => (dispatch) => {
  return dispatch({ type: RESET_LIST_REORDERING })
}

export const toggleIsReordering = () => (dispatch) => {
  return dispatch({ type: TOGGLE_IS_REORDERING })
}

export const setReorderableItems = (reorderedItems) => (dispatch) => {
  return dispatch({
    type: SET_REORDERED_LIST_ITEMS,
    data: reorderedItems,
  })
}

export const updateListItemOrder = (list) => async (dispatch, getState) => {
  // Update the sort order on the list items based on their new position
  const reorderedItems = selectReorderableItems(getState()).map((item, index) => ({
    ...item,
    sortOrder: index + 1,
  }))

  await putListSortOrderAPI(list.getServerID(), reorderedItems)

  const currentPageNumber = list.getPaginatedItems().number
  await dispatch(fetchList(list.getServerID(), new Page({ number: currentPageNumber, limit: LIST_ITEMS_PAGE_LIMIT })))
}
